// src/context/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  signInWithGoogle,
  logOut,
  onAuthChange,
  handleRedirectResult,
} from "../lib/firebaseAuth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const [loginState, setLoginState] = useState({
    isLoggingIn: false,
    error: null,
    hint: null,
    canRetry: false,
    useProductionLink: false,
    previewBlocked: false,
    errorCode: null,
  });

  const initDoneRef = useRef(false);

  // =========================
  // INIT AUTH (Google real - aplica a preview, prod y local)
  // =========================
  useEffect(() => {
    let unsub = () => {};

    const boot = async () => {
      // 1) Capturar resultado de redirect ANTES de suscribir, por si volvió
      //    de Google. handleRedirectResult tolera el caso "no hubo redirect".
      try {
        const redirected = await handleRedirectResult();
        if (redirected) {
          setUser(redirected);
        }
      } catch (e) {
        console.warn("⚠️ getRedirectResult fallo:", e);
      }

      // 2) Suscribir cambios de Auth (Google real). Se dispara al cargar
      //    sesión persistida y en cada login/logout.
      unsub = onAuthChange((u) => {
        setUser(u || null);

        if (!initDoneRef.current) {
          setAuthReady(true);
          initDoneRef.current = true;
        }

        if (u) {
          setLoginState({
            isLoggingIn: false,
            error: null,
            hint: null,
            canRetry: false,
            useProductionLink: false,
            previewBlocked: false,
            errorCode: null,
          });
        }
      });
    };

    boot();

    // Failsafe: nunca quedarnos colgados
    const t = setTimeout(() => {
      if (!initDoneRef.current) {
        setAuthReady(true);
        initDoneRef.current = true;
      }
    }, 5000);

    return () => {
      clearTimeout(t);
      unsub();
    };
  }, []);

  // =========
  // LOGIN
  // =========
  const login = async () => {
    setLoginState({
      isLoggingIn: true,
      error: null,
      hint: null,
      canRetry: false,
      useProductionLink: false,
      previewBlocked: false,
      errorCode: null,
    });

    try {
      const result = await signInWithGoogle();

      // ✅ PREVIEW DEV LOGIN
      if (result?.success && result?.user) {
        setUser(result.user);
        setAuthReady(true);
        setLoginState({
          isLoggingIn: false,
          error: null,
          hint: null,
          canRetry: false,
          useProductionLink: false,
          previewBlocked: false,
          errorCode: null,
        });
        return;
      }

      const friendly = result?.friendlyError || {};
      setLoginState({
        isLoggingIn: false,
        error: friendly.message || "Error de autenticación",
        hint: friendly.hint || "",
        canRetry: !!friendly.canRetry,
        useProductionLink: !!friendly.useProductionLink,
        previewBlocked: !!result?.previewBlocked,
        errorCode: result?.errorCode || null,
      });
    } catch (err) {
      setLoginState({
        isLoggingIn: false,
        error: `Error inesperado: ${err?.message || "desconocido"}`,
        hint: "",
        canRetry: true,
        useProductionLink: false,
        previewBlocked: false,
        errorCode: null,
      });
    }
  };

  // =========
  // LOGOUT
  // =========
  const logout = async () => {
    try {
      await logOut();
      setUser(null);
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const clearError = () => {
    setLoginState((prev) => ({
      ...prev,
      error: null,
      hint: null,
      canRetry: false,
      useProductionLink: false,
      previewBlocked: false,
      errorCode: null,
    }));
  };

  const value = useMemo(
    () => ({
      user,
      authReady,
      isAuthenticated: !!user,
      loginState,
      login,
      logout,
      clearError,
    }),
    [user, authReady, loginState]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de un AuthProvider");
  return ctx;
}

export default AuthContext;